// App.js
import { useState, useEffect, useRef } from 'react';
import {
  Title,
  Text,
  Group,
  Button,
  Textarea,
  Stack,
  Paper,
  Center,
  ThemeIcon,
  ActionIcon,
  Space,
  Alert,
  Loader,
} from '@mantine/core';
import {
  IconArrowUp,
  IconSquareFilled,
  IconAlertCircle
} from '@tabler/icons-react';
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';

import openaiLogo from './logos/openai.svg';
import googleLogo from './logos/google.svg';
import anthropicLogo from './logos/anthropic.svg';
import xaiLogo from './logos/xai.svg';

import { WhichBotLogo } from './WhichBotLogo';

import confetti from 'canvas-confetti';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getUserId } from './utils/userId';


// --- Configuration ---
const iconStyle = { width: 16, height: 16, display: 'block' };

const MODELS = [
  { id: 'gpt-51', name: 'GPT-5.1 Instant', provider: 'OpenAI', icon: <img src={openaiLogo} alt="OpenAI" style={iconStyle} /> },
  { id: 'gemini-2.5', name: 'Gemini 2.5 Flash', provider: 'Google', icon: <img src={googleLogo} alt="Google" style={iconStyle} /> },
  { id: 'claude-sonnet', name: 'Claude Sonnet 4.5', provider: 'Anthropic', icon: <img src={anthropicLogo} alt="Anthropic" style={iconStyle} /> },
  { id: 'grok', name: 'Grok 4.1 Fast', provider: 'xAI', icon: <img src={xaiLogo} alt="xAI" style={iconStyle} /> },
];

// --- Shared header shell (logos at top) ---
function AppShell({ children, rightSection, hasMessages }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '686px',
          padding: '20px',
          paddingBottom: hasMessages ? '0px' : '20px',
        }}
      >
        <Stack gap="lg" style={{ flex: 1 }}>
          {/* Horizontally centered div*/}
          <div style={{ margin: '0 auto' }}>
            <Group justify="center" align="center">
              <Group gap="xs" onClick={() => window.location.href = '/'} style={{ cursor: 'pointer' }}>
                <ThemeIcon variant="light" radius="xl" size="md">
                  <WhichBotLogo size={20} />
                </ThemeIcon>
                <Text fw={500} tt="uppercase" size="sm">
                  ModelGuessr
                </Text>
              </Group>
              {/* {rightSection} */}
            </Group>
            <Space h={4} />
            <Text ta='center' size='xs' c='dimmed'>
              Built by <a
                href="https://twitter.com/mdahardy"
                target="_blank"
                rel="noopener noreferrer"
                className="author-link"
              >
                Matt Hardy
              </a>
            </Text>
          </div>

          <div
            withBorder
            style={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </div>
        </Stack>
      </div>
    </div>
  );
}

// --- Landing page: centered ask-anything box ---
function LandingPage() {
  const [input, setInput] = useState('');
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const makeChatId = () => {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2, 10);
  };

  const startChat = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const chatId = makeChatId();
    navigate(`/c/${chatId}`, { state: { initialPrompt: trimmed } });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      startChat();
    }
  };

  return (
    <>
      <AppShell hasMessages={false}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? 16 : 24,
          }}
        >
          <div
            style={{
              flex: isMobile ? 1 : 'unset',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Stack gap={10} align="center">
              <Title order={2} size="xl" fw={400}>
                Guess the model
              </Title>
              <Text c="dimmed" size="sm" ta="center">
                We&apos;ll connect you to a random model. You try to guess which one it is.
              </Text>
              <Group gap={6} mt={4} justify="center" wrap="wrap">
                {MODELS.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: '1px solid #e2e4ea',
                      backgroundColor: '#fff',
                    }}
                  >
                    {m.icon}
                    <Text size="xs">{m.name}</Text>
                  </div>
                ))}
              </Group>
            </Stack>
          </div>

          <div
            style={{
              border: '1px solid #e2e4ea',
              borderRadius: '16px',
              padding: '10px 12px',
              backgroundColor: '#fff',
              marginBottom: isMobile ? 8 : 0,
            }}
          >
            <Textarea
              placeholder="Ask anything..."
              autosize
              maxRows={6}
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              variant="unstyled"
              styles={{
                input: {
                  border: 'none',
                  boxShadow: 'none',
                  padding: 0,
                  fontSize: 16,
                },
              }}
            />

            <Group justify="flex-end" mt={6}>
              <ActionIcon
                size="md"
                radius="md"
                variant="filled"
                onClick={startChat}
                disabled={!input.trim()}
              >
                <IconArrowUp size={16} />
              </ActionIcon>
            </Group>
          </div>
        </div>
      </AppShell>
    </>
  );
}

// --- Chat page: full chat + guessing ---
function ChatPage() {
  const { chatId } = useParams();
  const location = useLocation();

  const initialPromptRef = useRef(
    location.state && location.state.initialPrompt
      ? location.state.initialPrompt
      : null
  );

  // Chat state
  const [activeModelId, setActiveModelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Guessing UI
  const [guessValue, setGuessValue] = useState(null);
  const [guessResult, setGuessResult] = useState(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [shakeTick, setShakeTick] = useState(0);

  // Scrolling
  const messagesContainerRef = useRef(null);
  const bottomRef = useRef(null);

  const scrollToBottom = () => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ behavior: 'auto' });
  };

  const streamResponse = async (modelId, prompt, onChunk) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 30000); // 30 second timeout

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          prompt,
          oldMessages: messages,
          chatId,
          userId: getUserId(),
          sendTime: Date.now(),
          sessionId: sessionStorage.getItem('rtSessionId') || null,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Request failed');
      }

      if (!response.body) return;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          if (value) {
            onChunk(decoder.decode(value, { stream: true }));
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (e) {
      clearTimeout(timeoutId);
      if (e.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw e;
    }
  };

  const sendPrompt = async (promptText) => {
    if (!promptText.trim() || isStreaming || isReadOnly) return;

    setGuessResult(null);
    setError(null);

    const chosenModelId =
      activeModelId ?? MODELS[Math.floor(Math.random() * MODELS.length)].id;

    if (!activeModelId) {
      setActiveModelId(chosenModelId);
    }

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: promptText },
      { role: 'assistant', content: '' },
    ]);
    setIsStreaming(true);
    setIsWaitingForResponse(true);

    try {
      await streamResponse(chosenModelId, promptText, (chunk) => {
        setIsWaitingForResponse(false);
        setMessages((prev) => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          if (updated[lastIndex].role === 'assistant') {
            updated[lastIndex] = {
              ...updated[lastIndex],
              content: updated[lastIndex].content + chunk,
            };
          }
          return updated;
        });
      });
    } catch (e) {
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (
          updated[lastIndex].role === 'assistant' &&
          !updated[lastIndex].content
        ) {
          return updated.slice(0, -1);
        }
        return updated;
      });
      setError('Something went wrong. Please try again.');
    } finally {
      setIsStreaming(false);
      setIsWaitingForResponse(false);
    }
  };

  const handleSend = async () => {
    if (isStreaming || isReadOnly) return;
    const trimmed = input.trim();
    if (!trimmed) return;

    setInput('');
    await sendPrompt(trimmed);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleGuessSubmit = async (modelGuessId, event) => {
    if (!activeModelId) return;
    const isCorrect = modelGuessId === activeModelId;
    setGuessValue(modelGuessId);
    setGuessResult(isCorrect ? 'correct' : 'wrong');

    // Capture position BEFORE any async operations
    const rect = event.currentTarget.getBoundingClientRect();

    // Show UI feedback immediately
    if (!isCorrect) {
      setShakeTick((t) => t + 1);
    }

    if (isCorrect) {
      const x = (rect.left + rect.width / 2) / window.innerWidth;
      const y = (rect.top + rect.height / 2) / window.innerHeight;

      confetti({
        particleCount: 80,
        spread: 70,
        origin: { x, y },
        colors: ['#22c55e', '#4ade80'],
        ticks: 120,
      });
    }

    // Save guess to backend in background (fire and forget)
    fetch('/api/guess', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chatId,
        modelId: activeModelId,
        guessedModelId: modelGuessId,
        correct: isCorrect,
        userId: getUserId(),
      }),
    }).catch((e) => {
      console.error('Failed to save guess:', e);
    });
  };

  // Load existing chat on mount
  // Load existing chat on mount
  useEffect(() => {
    const loadChat = async () => {
      try {
        const res = await fetch(`/api/chat?chatId=${chatId}`);
        const data = await res.json();

        if (data.exists) {
          // Chat exists - load it (regardless of initialPrompt)
          setMessages(data.messages);
          setActiveModelId(data.modelId);
          setIsReadOnly(true);
          initialPromptRef.current = null; // Clear any stale initial prompt
        } else if (initialPromptRef.current) {
          // Chat doesn't exist and we have an initial prompt - send it
          const first = initialPromptRef.current;
          initialPromptRef.current = null;
          setIsLoading(false);
          sendPrompt(first);
          return; // Don't set isLoading false twice
        }
        // Else: new chat with no prompt - show empty state
      } catch (e) {
        console.error('Failed to load chat:', e);
      } finally {
        setIsLoading(false);
      }
    };

    loadChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length]);

  // // Auto-send first prompt passed from landing page (after loading completes)
  // useEffect(() => {
  //   if (isLoading || !initialPromptRef.current) return;
  //   const first = initialPromptRef.current;
  //   initialPromptRef.current = null;
  //   sendPrompt(first);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isLoading]);

  // Show loading state while checking for existing chat
  if (isLoading) {
    return (
      <AppShell hasMessages={false}>
        <Center style={{ flex: 1 }}>
          <Loader size="md" />
        </Center>
      </AppShell>
    );
  }

  return (
    <>
      <AppShell hasMessages={messages.length > 0}>
        <Stack
          gap="lg"
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          <Center>
            <Stack align="center">
              <Text c="dimmed" size="sm" ta="center">
                {isReadOnly
                  ? 'Viewing a saved conversation. Try to guess the model!'
                  : ` Try to guess the model (400 token output limit per response).`}
              </Text>
            </Stack>
          </Center>

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            style={{
              backgroundColor: '#ffffff',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {messages.length === 0 ? (
              <Center mih={160}>
                <Text c="dimmed" size="sm">
                  Start the conversation by asking a question below.
                </Text>
              </Center>
            ) : (
              <Stack gap="md">
                {messages.map((msg, index) => (
                  <Group
                    key={index}
                    align="flex-start"
                    justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                    wrap="nowrap"
                  >
                    {msg.role === 'assistant' &&
                      index === messages.length - 1 &&
                      isWaitingForResponse ? (
                      <Paper
                        radius="lg"
                        p="sm"
                        withBorder
                        style={{
                          backgroundColor: 'white',
                        }}
                      >
                        <Loader size="xs" type="dots" />
                      </Paper>
                    ) : (
                      <Paper
                        radius="lg"
                        p="sm"
                        withBorder={msg.role !== 'user'}
                        style={{
                          maxWidth: '80%',
                          backgroundColor:
                            msg.role === 'user' ? '#f6f6f6' : 'white',
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ node, ...props }) => (
                              <Text size="sm" {...props} />
                            ),
                            li: ({ node, ...props }) => (
                              <li>
                                <Text component="span" size="sm" {...props} />
                              </li>
                            ),
                          }}
                        >
                          {msg.content +
                            (isStreaming &&
                              index === messages.length - 1 &&
                              msg.role === 'assistant'
                              ? ' ▋'
                              : '')}
                        </ReactMarkdown>
                      </Paper>
                    )}
                  </Group>
                ))}
                <div ref={bottomRef} />
              </Stack>
            )}
          </div>

          {/* Input + guessing UI */}
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              background:
                'linear-gradient(to top, #ffffff 0%, rgba(255,255,255,1) 80%, rgba(255,255,255,0) 100%)',
            }}
          >
            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                withCloseButton
                onClose={() => setError(null)}
                mb="sm"
                radius="md"
              >
                {error}
              </Alert>
            )}

            {!isReadOnly && (
              <div
                style={{
                  border: '1px solid #e2e4ea',
                  borderRadius: '16px',
                  padding: '10px 12px',
                  backgroundColor: '#fff',
                }}
              >
                <Textarea
                  placeholder="Ask follow-up..."
                  autosize
                  radius="md"
                  size="md"
                  minRows={1}
                  maxRows={4}
                  value={input}
                  onChange={(e) => setInput(e.currentTarget.value)}
                  onKeyDown={handleKeyDown}
                  variant="unstyled"
                  styles={{
                    input: {
                      border: 'none',
                      boxShadow: 'none',
                      padding: 0,
                      fontSize: 16,
                    },
                  }}
                />

                <Group justify="flex-end" mt={6}>
                  <ActionIcon
                    size="md"
                    radius="md"
                    variant="filled"
                    onClick={handleSend}
                    disabled={!input.trim() || isStreaming}
                  >
                    {isStreaming ? (
                      <IconSquareFilled size={16} />
                    ) : (
                      <IconArrowUp size={16} />
                    )}
                  </ActionIcon>
                </Group>
              </div>
            )}

            <Space h="md" />

            <Paper withBorder={false} radius="md" p="md" bg="white">
              <Text size="sm" c="dimmed" mb={6} ta="center">
                Which model do you think you're chatting with?
              </Text>

              <Space h="5" />

              <Group justify="center" gap="xs" wrap="wrap">
                {MODELS.map((m) => {
                  const isChosen = guessValue === m.id;
                  let color = 'gray';
                  if (guessResult && isChosen) {
                    color = guessResult === 'correct' ? 'green' : 'red';
                  }
                  return (
                    <Button
                      key={`${m.id}-${isChosen && guessResult === 'wrong' ? shakeTick : 0
                        }`}
                      size="xs"
                      radius="md"
                      variant="outline"
                      color={color}
                      leftSection={m.icon}
                      className={
                        isChosen && guessResult === 'wrong'
                          ? 'button-wrong'
                          : undefined
                      }
                      onClick={(event) => handleGuessSubmit(m.id, event)}
                    >
                      {m.name}
                    </Button>
                  );
                })}
              </Group>
              <Center mt="xs">
                <Text
                  size="sm"
                  c={guessResult === 'correct' ? 'green' : 'red'}
                  fw={500}
                  style={{
                    opacity: guessResult ? 1 : 0,
                    pointerEvents: guessResult ? 'auto' : 'none',
                  }}
                >
                  {guessResult === 'correct'
                    ? 'Correct - you guessed the model!'
                    : guessResult
                      ? `Incorrect - it's not ${MODELS.find((m) => m.id === guessValue)?.name
                      }.`
                      : 'Placeholder'}
                </Text>
              </Center>
            </Paper>
          </div>
        </Stack>
      </AppShell>
    </>
  );
}

// --- Root App with routes ---
export default function App() {
  const showBadge = useMediaQuery('(min-width: 1200px)');
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/c/:chatId" element={<ChatPage />} />
      </Routes>
      {showBadge && <div className='roundtable-badge' style={{ position: 'fixed', bottom: '12px', right: '12px' }}></div>}

    </BrowserRouter>
  );
}